/**
 * Atendimentos Module Routes — painel call-center 3 colunas (PRD 4, Story 4.1.4).
 *
 * Permissão: ``atendimentos:read`` (seed RBAC vem em Story 4.6.x).
 * Até lá, login com role admin (que tem `*:*`) consegue acessar.
 *
 * Inclui também tela admin de Comunicado Interativo Multi-Mídia
 * (PRD 4, Story 4.6.3) sob a permissão ``interactive_broadcasts:read``.
 */

import { lazy } from "react";
import { Navigate } from "react-router-dom";

import { ModuleRoutes } from "../types";

const InteractiveBroadcastsPage = lazy(() => import("@/pages/admin/InteractiveBroadcastsPage"));

const CallbackQueuePage = lazy(() => import("@/pages/atendimentos/CallbackQueuePage"));

const NpsPanelPage = lazy(() => import("@/pages/atendimentos/NpsPanelPage"));

const AuditoriasPage = lazy(() => import("@/pages/atendimentos/AuditoriasPage"));

const SlaMetricsPage = lazy(() => import("@/pages/atendimentos/SlaMetricsPage"));

// E9.2.1 — Admin de Templates de resposta + Tags do catálogo.
const TemplatesAdminPage = lazy(() => import("@/pages/atendimentos/TemplatesAdminPage"));

// E9.2.3 — Admin de Bot Triagem (config + menu-tree viewer).
const BotTriageConfigPage = lazy(() => import("@/pages/atendimentos/BotTriageConfigPage"));
const GravacaoConfigPage = lazy(() => import("@/pages/atendimentos/GravacaoConfigPage"));

export const atendimentosRoutes: ModuleRoutes = {
  moduleId: "atendimentos",
  basePath: "/atendimentos",
  routes: [
    {
      // 25/09/2026 — o painel mora na aba "Atendimento externo" de /mensagens,
      // ao lado do chat interno. A rota fica como ATALHO: menu do celular,
      // atalhos da Home e favoritos ainda apontam para cá. Só a rota exata —
      // as sub-rotas abaixo continuam páginas próprias.
      path: "/atendimentos",
      element: <Navigate to="/mensagens?aba=atendimento" replace />,
      permission: { resource: "atendimentos", action: "read" },
    },
    {
      path: "/atendimentos/callbacks",
      element: <CallbackQueuePage />,
      permission: { resource: "atendimentos", action: "read" },
    },
    {
      path: "/atendimentos/nps",
      element: <NpsPanelPage />,
      permission: { resource: "atendimentos", action: "read" },
    },
    {
      path: "/atendimentos/auditorias",
      element: <AuditoriasPage />,
      permission: { resource: "atendimentos", action: "read" },
    },
    {
      // Story E9.1.4 — SLA & Métricas dashboard.
      path: "/atendimentos/sla-metrics",
      element: <SlaMetricsPage />,
      permission: { resource: "atendimentos", action: "read" },
    },
    {
      // Story E9.2.1 — Admin de Templates de resposta + Tags.
      path: "/atendimentos/templates",
      element: <TemplatesAdminPage />,
      permission: { resource: "atendimento_templates", action: "read" },
    },
    {
      // Story E9.2.3 — Admin de Bot Triagem (config + menu YAML viewer).
      path: "/atendimentos/bot",
      element: <BotTriageConfigPage />,
      permission: { resource: "atendimentos", action: "bot_read" },
    },
    {
      // Alavanca runtime da gravação (07/08/2026): ligar sem redeploy quando a
      // máquina crescer. `calls:supervisor` porque decidir que a EMPRESA grava
      // é compliance + capacidade, não operação do dia a dia.
      path: "/atendimentos/gravacao",
      element: <GravacaoConfigPage />,
      permission: { resource: "calls", action: "supervisor" },
    },
    {
      path: "/admin/interactive-broadcasts",
      element: <InteractiveBroadcastsPage />,
      permission: { resource: "interactive_broadcasts", action: "read" },
    },
  ],
};
